import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError, timeout } from 'rxjs';
import { APPLY_DEFAULT_HTTP_TIMEOUT, DEFAULT_HTTP_TIMEOUT_MS, HTTP_TIMEOUT_MS } from '../http-context/http-timeout';
import { HttpTimeoutError } from '../models/http-timeout-error';
import { ConfigService } from '../services/config/config.service';

/**
 * Bounds GETs and explicitly opted-in read-style requests so a stalled connection fails fast with a
 * classified error instead of spinning until the browser gives up (60s+ on mobile). Mutations are
 * not timed by default: cancelling the browser side of a slow write can still leave the server
 * commit in flight, which risks duplicate patient actions on retry.
 *
 * **Registered last in `app.config.ts`, on purpose.** DI interceptor order makes the first-registered
 * interceptor outermost, so registering this one after `MsalInterceptor` puts it innermost —
 * closest to the actual XHR. That matters twice over:
 *
 *  - The `HttpTimeoutError` is thrown *inside* `AppInsightsInterceptor` and `ErrorInterceptor`, so
 *    both still see it and their existing telemetry/routing behaviour applies. Registered outermost
 *    it would bypass them entirely.
 *  - Unsubscribing genuinely cancels the in-flight request rather than abandoning it.
 *
 * The tradeoff is that MSAL token acquisition happens *outside* this timeout, so total wall-clock
 * for a request that needs an interactive token refresh can exceed the budget. That is deliberate:
 * a redirect for consent is not a hung request and must not be cancelled.
 */
@Injectable()
export class HttpTimeoutInterceptor implements HttpInterceptor {
  private configService = inject(ConfigService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const timeoutMs = this.resolveTimeoutMs(request);

    if (timeoutMs === false) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      // `each` rather than `first`: with a single-response HttpClient observable the two are
      // equivalent, but `each` is the correct semantic if a call ever opts into reportProgress,
      // where the budget should bound idle time and not total download time.
      timeout({
        each: timeoutMs,
        with: () => throwError(() => new HttpTimeoutError(request.url, timeoutMs)),
      })
    );
  }

  /**
   * Per-request override wins; otherwise GETs and opted-in non-GET reads use the deploy-injected
   * `httpTimeoutMs`; otherwise the compile-time default. The default is load-bearing rather than
   * defensive: `ConfigService` fetches `/config.json` through `HttpClient`, so the very first
   * requests of the session are resolved before any config exists.
   */
  private resolveTimeoutMs(request: HttpRequest<unknown>): number | false {
    const override = request.context.get(HTTP_TIMEOUT_MS);
    if (override !== null) {
      return override;
    }

    if (request.method !== 'GET' && !request.context.get(APPLY_DEFAULT_HTTP_TIMEOUT)) {
      return false;
    }

    const configured = this.configService.config?.httpTimeoutMs;

    return typeof configured === 'number' && Number.isFinite(configured) && configured > 0
      ? configured
      : DEFAULT_HTTP_TIMEOUT_MS;
  }
}
